import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'
import {
  useProductSource,
  type ProductSourceDto,
  type SourceType
} from '../../../viewmodels/useProductSource'

/**
 * `/products/source` — per-PRODUCT Make vs Buy.
 *
 * Lists every product and marks each made in-house (Make) or bought finished
 * (Buy). A Make product's variants cost from their raw materials (Product
 * Materials screen); marking Buy is informational here — no supplier/pricing.
 */
export function ProductSourcePage(): JSX.Element {
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.PRODUCTSOURCE, 'edit')
  const vm = useProductSource()

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Product Source — Uyyamadilu</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="space-y-1">
        <h1 className="font-heading text-h1 text-charcoal">Product Source</h1>
        <p className="font-body text-body text-text-secondary">
          Mark each product as made in-house or bought finished.
        </p>
      </header>

      {vm.error !== null ? <Alert tone="error">{vm.error}</Alert> : null}

      {vm.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : vm.products.length === 0 ? (
        <p className="font-body text-body-sm text-text-secondary">No products yet.</p>
      ) : (
        <ul className="space-y-3">
          {vm.products.map((p) => (
            <ProductRow key={p.id} product={p} canEdit={canEdit} setSource={vm.setSource} />
          ))}
        </ul>
      )}
    </div>
  )
}

function ProductRow({
  product,
  canEdit,
  setSource
}: {
  readonly product: ProductSourceDto
  readonly canEdit: boolean
  readonly setSource: (id: string, type: SourceType) => Promise<{ ok: true } | { ok: false; message: string }>
}): JSX.Element {
  // The toggle is local UI only; the choice persists when Save is clicked.
  const [choice, setChoice] = useState<SourceType>(product.source_type)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const dirty = choice !== product.source_type

  async function save(): Promise<void> {
    if (!dirty || busy) return
    setErr(null)
    setBusy(true)
    const r = await setSource(product.id, choice)
    setBusy(false)
    if (r.ok) setSavedAt(Date.now())
    else setErr(r.message)
  }

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-bg-primary p-4 shadow-sm">
      <div className="min-w-0">
        <p className="font-heading text-h4 text-charcoal truncate">{product.name}</p>
        <p className="font-body text-body-xs text-text-secondary">{product.category_name}</p>
        {err !== null ? <span className="font-body text-body-xs text-terra">{err}</span> : null}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 rounded-full border border-border p-1">
          {(['make', 'buy'] as const).map((t) => (
            <button
              key={t}
              type="button"
              disabled={!canEdit || busy || choice === t}
              onClick={() => {
                setChoice(t)
                setSavedAt(null)
                setErr(null)
              }}
              className={`rounded-full px-4 py-1 font-body text-body-sm transition ${
                choice === t
                  ? 'bg-forest text-charcoal shadow-sm'
                  : 'text-text-secondary hover:text-charcoal'
              }`}
            >
              {t === 'make' ? 'Make' : 'Buy'}
            </button>
          ))}
        </div>
        {canEdit ? (
          <Button type="button" size="sm" variant="primary" disabled={!dirty || busy} onClick={() => void save()}>
            {busy ? 'Saving…' : savedAt !== null && !dirty ? 'Saved' : 'Save'}
          </Button>
        ) : null}
      </div>
    </li>
  )
}
